import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {FiX, FiPlay, FiCheck, FiStar, FiPhone, FiMail, FiUser, FiBriefcase, FiChevronDown, FiSearch, FiFilter, FiMessageCircle} from 'react-icons/fi';
import './App.css';
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import WorkshopTimer from './components/WorkshopTimer';
import {useWorkshopSchedule} from './hooks/useWorkshopSchedule';

// Thank You Page URL
const THANK_YOU_PAGE_URL = 'https://thxwomenscoaches.10xaifounder.com/';

// Pabbly Webhook URL
const WEBHOOK_URL = 'https://connect.pabbly.com/webhook-listener/webhook/IjU3NjIwNTY1MDYzMDA0MzQ1MjZmIg_3D_3D_pc/IjU3NjcwNTZjMDYzNzA0MzA1MjZlNTUzNjUxMzEi_pc';

// WhatsApp Support Number
const WHATSAPP_NUMBER = '+919940890569';
const WHATSAPP_MESSAGE = encodeURIComponent(
  'Hi! I need help with the Life Coach Workshop registration for women.'
);

// Animation Variants
const containerVariants = {
  hidden: {opacity: 0},
  visible: {
    opacity: 1,
    transition: {staggerChildren: 0.1, ease: 'easeInOut'}
  }
};

const itemVariants = {
  hidden: {opacity: 0, y: 20},
  visible: {
    opacity: 1,
    y: 0,
    transition: {type: 'spring', stiffness: 100, damping: 12}
  }
};

const sectionFadeIn = {
  initial: {opacity: 0, y: 30},
  whileInView: {opacity: 1, y: 0},
  transition: {duration: 0.7, ease: 'easeInOut'},
  viewport: {once: true, amount: 0.2}
};

// Video testimonials data - WOMEN LIFE COACH FOCUSED
const testimonialVideos = [
  {
    id: 1,
    url: 'https://youtu.be/vRzrNK9XPmQ?si=HcsiRvsrjOQhzfiO',
    embedId: 'vRzrNK9XPmQ',
    thumbnail: 'https://img.youtube.com/vi/vRzrNK9XPmQ/maxresdefault.jpg',
    title: 'How I Built My Life Coaching Practice While Managing Family',
    name: 'Priya Sharma'
  },
  {
    id: 2,
    url: 'https://www.youtube.com/watch?v=v3EEtDIaC3w',
    embedId: 'v3EEtDIaC3w',
    thumbnail: 'https://img.youtube.com/vi/v3EEtDIaC3w/maxresdefault.jpg',
    title: 'From Housewife to Confident Life Coach - My Journey',
    name: 'Anitha Menon'
  },
  {
    id: 3,
    url: 'https://youtu.be/IrfPwkcSTB0?si=4zR7J5O2aIbdr7k0',
    embedId: 'IrfPwkcSTB0',
    thumbnail: 'https://img.youtube.com/vi/IrfPwkcSTB0/maxresdefault.jpg',
    title: 'Earning ₹2 Lakhs Monthly as a Part-Time Life Coach',
    name: 'Divya Krishnan'
  },
  {
    id: 4,
    url: 'https://youtu.be/F26JiMS-w4I?si=5CoJ4w0AnUAMPYZm',
    embedId: 'F26JiMS-w4I',
    thumbnail: 'https://img.youtube.com/vi/F26JiMS-w4I/maxresdefault.jpg',
    title: 'Balancing Family and Life Coaching Career',
    name: 'Lakshmi Iyer'
  },
  {
    id: 5,
    url: 'https://youtu.be/yWzJt7jv5CM?si=cFmZMEoNQgvFxmh4',
    embedId: 'yWzJt7jv5CM',
    thumbnail: 'https://img.youtube.com/vi/yWzJt7jv5CM/maxresdefault.jpg',
    title: 'How I Made ₹30 Lakhs in a Year as a Life Coach',
    name: 'Meera Reddy'
  },
  {
    id: 6,
    url: 'https://youtube.com/shorts/L0jJ2NHIQ8w?si=vDvB1EIcSDn0lzHX',
    embedId: 'L0jJ2NHIQ8w',
    thumbnail: 'https://img.youtube.com/vi/L0jJ2NHIQ8w/maxresdefault.jpg',
    title: 'The Secret Behind My Life Coaching Success',
    name: 'Sangeetha Rajan'
  }
];

// Client Feedback data - WOMEN LIFE COACH FOCUSED
const clientFeedbacks = [
  {
    id: 1,
    name: 'Kavita Sharma',
    role: 'Women Empowerment Life Coach',
    image: 'https://storage.files-vault.com/uploads/1760942061-nsFEgCbsBd.webp',
    quote: 'From being "just a housewife" to earning ₹8 Lakhs in 6 months as a life coach. My family now respects my work and I have my own identity.'
  },
  {
    id: 2,
    name: 'Priya Menon',
    role: 'Relationship & Family Life Coach',
    image: 'https://storage.files-vault.com/uploads/1760941910-ht57JBdJ4f.webp',
    quote: 'I work around my kids\' school timings and still built a ₹15+ Lakhs coaching practice. Financial independence + family time — both are possible!'
  },
  {
    id: 3,
    name: 'Anitha Krishnan',
    role: 'Career Transition Life Coach',
    image: 'https://storage.files-vault.com/uploads/1760942062-cWbhjZc7n9.webp',
    quote: 'I was tired of office politics and family pressure. Now I help working women transition into meaningful careers. Earning more than my corporate salary with flexibility.'
  },
  {
    id: 4,
    name: 'Divya Iyer',
    role: 'Confidence & Self-Worth Coach',
    image: 'https://storage.files-vault.com/uploads/1760942064-I6HBtoRq5t.webp',
    quote: 'My husband used to question "Why do you need this?" Now he sees me helping 100+ women build confidence and earning ₹10 Lakhs annually. Respect + income + purpose.'
  },
  {
    id: 5,
    name: 'Lakshmi Reddy',
    role: 'Emotional Wellness Life Coach',
    image: 'https://storage.files-vault.com/uploads/1760942066-tiqyCho8Gf.webp',
    quote: 'I always helped friends with their problems for free. Now I do it professionally, earn well, and feel valued. My voice has value, my experience has demand.'
  },
  {
    id: 6,
    name: 'Sangeetha Nair',
    role: 'Work-Life Balance Coach',
    image: null,
    quote: 'No more asking husband for money. I built a flexible life coaching practice that fits around family responsibilities. Purpose + dignity + financial independence.'
  }
];

// FAQ data - WOMEN LIFE COACH FOCUSED
const faqData = [
  {
    id: 1,
    question: 'When and where is this 3-hour workshop happening?',
    answer: "This is a **3-hour LIVE online workshop** held **every Sunday from 10:00 AM to 1:00 PM IST** (India Standard Time). Once you register, you'll receive the secure joining link via email and WhatsApp."
  },
  {
    id: 2,
    question: 'Is this suitable for housewives and working women?',
    answer: 'Yes! This is specifically designed for women (housewives and working women) who want to build a flexible life coaching career without sacrificing family time. You can work around your responsibilities.'
  },
  {
    id: 3,
    question: 'Do I need any qualifications or certifications?',
    answer: 'No formal qualifications needed. Your life experience, emotional intelligence, and natural listening skills are your biggest strengths. We teach you how to turn these into a professional coaching practice.'
  },
  {
    id: 4,
    question: "Will I get recordings if I can't attend live?",
    answer: 'To maintain the exclusivity and high-impact nature of the live session, recordings will not be provided. We encourage you to attend live to ask questions about your specific situation.'
  },
  {
    id: 5,
    question: 'Can I really earn as a life coach while managing family?',
    answer: 'Absolutely! Many women in our community are earning ₹5-25 Lakhs annually while working flexible hours around family time. Life coaching allows you to choose your schedule and work from home.'
  },
  {
    id: 6,
    question: 'What happens after I register?',
    answer: "You'll immediately receive a confirmation email and WhatsApp message with your unique joining link, bonus resources for women life coaches, and instructions. You'll also get helpful reminders before the workshop."
  },
  {
    id: 7,
    question: 'What if my family doesn\'t support this?',
    answer: "Many women face this. In the workshop, we address how to communicate your goals to family and build a coaching practice that doesn't compromise family time. When they see results, support naturally follows."
  },
  {
    id: 8,
    question: 'Is this really FREE?',
    answer: "Yes! This 3-hour workshop is completely FREE for women who want to explore life coaching as a career. No hidden charges. We're committed to helping women build meaningful, flexible careers."
  }
];

// Expanded Country Codes List
const countryCodes = [
  {code: '+91', country: 'IN', name: 'India', flag: '🇮🇳'},
  {code: '+1', country: 'US', name: 'USA', flag: '🇺🇸'},
  {code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾'},
  {code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬'},
  {code: '+44', country: 'GB', name: 'UK', flag: '🇬🇧'},
  {code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪'},
  {code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺'},
  {code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦'}
];

// Tracking data extraction function
const getTrackingData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || 'en-US';

  let detectedCountry = 'Unknown';
  if (timezone && timezone.includes('/')) {
    const cityTimezone = timezone.split('/')[1];
    if (cityTimezone) {
      const timezoneCountryMap = {
        Kolkata: 'India',
        Mumbai: 'India',
        Delhi: 'India',
        Chennai: 'India',
        Bangalore: 'India',
        New_York: 'USA',
        Los_Angeles: 'USA',
        Chicago: 'USA',
        London: 'UK',
        Paris: 'France',
        Berlin: 'Germany',
        Tokyo: 'Japan',
        Singapore: 'Singapore',
        Dubai: 'UAE'
      };
      detectedCountry = timezoneCountryMap[cityTimezone] || 'Unknown';
    }
  }

  return {
    campaign_id: urlParams.get('campaign_id') || '',
    adset_id: urlParams.get('adset_id') || '',
    ad_id: urlParams.get('ad_id') || '',
    fbclid: urlParams.get('fbclid') || '',
    gclid: urlParams.get('gclid') || '',
    campaign_name: urlParams.get('campaign_name') || 'Women Life Coach Workshop Campaign',
    adset_name: urlParams.get('adset_name') || 'Women Life Coach Workshop Ad Set',
    ad_name: urlParams.get('ad_name') || 'Women Life Coach Workshop Ad',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || '',
    source: urlParams.get('source') || 'organic',
    medium: urlParams.get('medium') || '',
    country: detectedCountry,
    timezone: timezone,
    locale: locale,
    referrer: document.referrer || '',
    page_url: window.location.href,
    page_title: document.title,
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    timestamp: new Date().toISOString(),
    session_id: sessionStorage.getItem('session_id') || Date.now().toString()
  };
};

// Generate workshop batch ID
const generateWorkshopBatchId = (workshopDate) => {
  if (!workshopDate) return 'BATCH_UNKNOWN';
  const date = new Date(workshopDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `BATCH_${year}${month}${day}`;
};

// Get batch name
const getBatchName = (workshopDate) => {
  if (!workshopDate) return 'Sunday Unknown';
  const date = new Date(workshopDate);
  const dayNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];
  const dayName = dayNames[date.getDay()];
  const day = date.getDate();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[date.getMonth()];
  return `${dayName} ${day}${getDaySuffix(day)} ${monthName}`;
};

// Get webinar batch ISO 8601 format
const getWebinarBatchISO = (workshopDate) => {
  if (!workshopDate) return new Date().toISOString();
  const date = new Date(workshopDate);
  date.setHours(10, 0, 0, 0);
  return date.toISOString();
};

function getDaySuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// Searchable Country Code Component
const CountryOption = React.memo(({country, onSelect}) => (
  <li
    className="country-option"
    onClick={() => onSelect(country.code)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onSelect(country.code);
    }}
    role="option"
    tabIndex={0}
  >
    <span className="country-flag">{country.flag}</span>
    <span className="country-name">{country.name}</span>
    <span className="country-code">{country.code}</span>
  </li>
));

function SearchableCountryCodeSelect({value, onChange, options}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCountry = useMemo(
    () => options.find((c) => c.code === value) || options[0],
    [value, options]
  );

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(
      (country) =>
        country.name.toLowerCase().includes(lowerSearch) ||
        country.code.includes(lowerSearch) ||
        country.country.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (code) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="country-select-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="country-flag">{selectedCountry.flag}</span>
        <span>{selectedCountry.code}</span>
        <FiChevronDown className={`chevron-icon ${isOpen ? 'expanded' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="dropdown-menu"
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -10}}
            transition={{duration: 0.2}}
          >
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search country..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ul className="dropdown-list" role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((country) => (
                  <CountryOption
                    key={country.country}
                    country={country}
                    onSelect={handleSelect}
                  />
                ))
              ) : (
                <li className="country-option-empty">No results found</li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// WhatsApp Support Button Component
function WhatsAppSupportButton() {
  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Contact', {
        content_name: 'WhatsApp Support Click',
        content_category: 'Customer Support'
      });
    }
  };

  return (
    <motion.button
      className="whatsapp-support-button"
      onClick={handleWhatsAppClick}
      initial={{scale: 0}}
      animate={{scale: 1}}
      transition={{type: 'spring', stiffness: 260, damping: 20, delay: 2}}
      whileHover={{scale: 1.1}}
      whileTap={{scale: 0.95}}
      aria-label="Contact us on WhatsApp"
    >
      <FiMessageCircle className="whatsapp-icon" />
      <span className="whatsapp-tooltip">Need Help? Chat with us!</span>
    </motion.button>
  );
}

function App() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    profession: '',
    consent: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState({});

  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  const {workshopDetails} = useWorkshopSchedule();

  useEffect(() => {
    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Date.now().toString());
    }
  }, []);

  useEffect(() => {
    if (isFormModalOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFormModalOpen(false);
        setSelectedVideo(null);
      }
    };

    if (isFormModalOpen || selectedVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFormModalOpen, selectedVideo]);

  const handleFaqSearch = useCallback(
    (value) => {
      setFaqSearch(value);
      if (searchTimeout) clearTimeout(searchTimeout);
      const newTimeout = setTimeout(() => {}, 300);
      setSearchTimeout(newTimeout);
    },
    [searchTimeout]
  );

  const filteredFaqs = useMemo(() => {
    if (!faqSearch.trim()) return faqData;
    return faqData.filter(
      (faq) =>
        faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.answer.toLowerCase().includes(faqSearch.toLowerCase())
    );
  }, [faqSearch]);

  const filteredFeedbacks = useMemo(() => {
    if (selectedRoleFilter === 'all') return clientFeedbacks;
    return clientFeedbacks.filter((feedback) =>
      feedback.role.toLowerCase().includes(selectedRoleFilter.toLowerCase())
    );
  }, [selectedRoleFilter]);

  const uniqueRoles = useMemo(
    () => ['all', ...new Set(clientFeedbacks.map((f) => f.role))],
    []
  );

  const toggleFaq = (faqId) => setExpandedFaq(expandedFaq === faqId ? null : faqId);
  const toggleFeedbackExpanded = (feedbackId) =>
    setExpandedFeedback((prev) => ({...prev, [feedbackId]: !prev[feedbackId]}));

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{7,15}$/.test(formData.phone.replace(/\s/g, '')))
      errors.phone = 'Please enter a valid phone number';
    if (!formData.profession.trim())
      errors.profession = 'Please select your current situation';
    if (!formData.consent) errors.consent = 'Please agree to the terms';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const phoneWithCountryCode = `${formData.countryCode}${formData.phone}`;
      const trackingData = getTrackingData();
      const workshopBatchId = generateWorkshopBatchId(workshopDetails.date);
      const workshopBatchName = getBatchName(workshopDetails.date);
      const webinarBatchISO = getWebinarBatchISO(workshopDetails.date);

      const selectedCountry =
        countryCodes.find((c) => c.code === formData.countryCode) || {
          country: 'Unknown',
          name: 'Unknown'
        };

      const webhookPayload = {
        first_name: formData.name,
        email: formData.email,
        phone_no: phoneWithCountryCode,
        profession: formData.profession,
        workshop_batch: workshopBatchId,
        batch_name: workshopBatchName,
        batch_date: workshopDetails.formattedDate,
        batch_time: workshopDetails.timeString,
        webinar_batch_iso: webinarBatchISO,
        amount: '0',
        payment_status: 'free',
        registration_type: 'free_workshop',
        campaign_id: trackingData.campaign_id,
        adset_id: trackingData.adset_id,
        ad_id: trackingData.ad_id,
        campaign_name: trackingData.campaign_name,
        adset_name: trackingData.adset_name,
        ad_name: trackingData.ad_name,
        fbclid: trackingData.fbclid,
        gclid: trackingData.gclid,
        utm_source: trackingData.utm_source,
        utm_medium: trackingData.utm_medium,
        utm_campaign: trackingData.utm_campaign,
        utm_term: trackingData.utm_term,
        utm_content: trackingData.utm_content,
        country: trackingData.country,
        timezone: trackingData.timezone,
        locale: trackingData.locale,
        phone_country_code: selectedCountry.country,
        phone_country_name: selectedCountry.name,
        phone_dial_code: formData.countryCode,
        source: trackingData.source,
        medium: trackingData.medium,
        referrer: trackingData.referrer,
        page_url: trackingData.page_url,
        session_id: trackingData.session_id,
        registration_timestamp: trackingData.timestamp,
        product_name: 'Women Life Coach Workshop',
        currency: 'INR'
      };

      try {
        const webhookResponse = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(webhookPayload)
        });

        if (!webhookResponse.ok) {
          console.warn('Webhook response not OK:', webhookResponse.status);
        }
        console.log('✅ Webhook data sent:', webhookPayload);
      } catch (webhookError) {
        console.warn('⚠️ Webhook submission failed:', webhookError);
      }

      if (typeof fbq !== 'undefined') {
        fbq('track', 'CompleteRegistration', {
          content_name: 'Women Life Coach Workshop Registration',
          content_category: 'Workshop',
          value: 0,
          currency: 'INR',
          ...(trackingData.campaign_id && {campaign_id: trackingData.campaign_id}),
          ...(trackingData.adset_id && {adset_id: trackingData.adset_id}),
          ...(trackingData.ad_id && {ad_id: trackingData.ad_id}),
          custom_batch_id: workshopBatchId,
          custom_batch_name: workshopBatchName
        });
      }

      setShowSuccess(true);
      setIsFormModalOpen(false);
      setFormData({
        name: '',
        email: '',
        countryCode: '+91',
        phone: '',
        profession: '',
        consent: false
      });

      setTimeout(() => {
        window.location.href = THANK_YOU_PAGE_URL;
      }, 1500);
    } catch (error) {
      console.error('❌ Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({...prev, [field]: value}));
    if (formErrors[field]) setFormErrors((prev) => ({...prev, [field]: ''}));
  };

  const getInitials = (name) =>
    name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const handleManualModalOpen = () => {
    setIsFormModalOpen(true);
  };

  return (
    <>
      <div className="app">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              className="success-toast"
              initial={{opacity: 0, y: -50}}
              animate={{opacity: 1, y: 0}}
              exit={{opacity: 0, y: -50}}
              transition={{type: 'spring', stiffness: 300, damping: 20}}
            >
              <FiCheck className="success-icon" />
              Registration successful! Redirecting to confirmation page...
            </motion.div>
          )}
        </AnimatePresence>

        <WhatsAppSupportButton />

        <HeroSection openRegistration={handleManualModalOpen} />

        <ProblemSection />

        <SolutionSection />

        {/* Mentor Section - WOMEN FOCUSED */}
        <section className="mentor-section py-16 bg-[#0a2540] text-gray-200">
          <div className="container mx-auto px-4">
            <motion.div
              className="mentor-content grid items-center gap-10 lg:grid-cols-2"
              initial="initial"
              whileInView="whileInView"
              transition={sectionFadeIn.transition}
              viewport={sectionFadeIn.viewport}
            >
              <div className="flex justify-center lg:justify-end">
                <motion.div
                  className="rounded-3xl border-2 border-[#8b5cf6] p-3 shadow-lg shadow-[#8b5cf6]/20 bg-[#312e81]"
                  whileHover={{scale: 1.02}}
                  transition={{duration: 0.3}}
                >
                  <img
                    src="https://storage.files-vault.com/uploads/1761032692-VSdwsp8AG0.png"
                    alt="Abdul Hakkim - Women Life Coach Mentor"
                    loading="lazy"
                    className="rounded-2xl w-full max-w-[360px] md:max-w-[400px] object-cover"
                  />
                </motion.div>
              </div>

              <div className="mentor-info max-w-xl mx-auto lg:mx-0">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-[#fbbf24] mb-6 flex items-center gap-2"
                  {...sectionFadeIn}
                >
                  🎯 <span>Meet Your Mentor</span>
                </motion.h2>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mb-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{delay: 0.1, ...sectionFadeIn.transition}}
                >
                  நான் Abdul Hakkim. பல women திறமையும் emotional intelligence-உம் இருந்தும், scalable system இல்லாததால் தங்கள் potential-ஐ realize பண்ண முடியாமல் struggle பண்றதை பார்த்திருக்கிறேன்.
                </motion.p>

                <motion.ul
                  className="space-y-4 text-gray-300 text-base md:text-lg"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{once: true, amount: 0.2}}
                >
                  {[
                    {
                      emoji: '👉',
                      color: 'text-yellow-400',
                      title: 'The Real Problem:',
                      desc: 'Life experience இருக்கு, emotional intelligence இருக்கு, but system இல்லாததால் identity + income கிடைக்கலை.'
                    },
                    {
                      emoji: '✅',
                      color: 'text-green-400',
                      title: 'My Solution:',
                      desc: 'நான் உருவாக்கிய "Women Life Coach System" உங்கள் natural skills-ஐ respected career-ஆக மாற்றும்.'
                    },
                    {
                      emoji: '📈',
                      color: 'text-blue-400',
                      title: 'Proven Results:',
                      desc: 'Helped 100+ Women build flexible life coaching careers earning ₹5-25 Lakhs annually around family time.'
                    },
                    {
                      emoji: '🗣️',
                      color: 'text-purple-400',
                      title: 'Family-Friendly:',
                      desc: 'Tamil-ல எளிய முறையில் life coaching system கற்றுக்கொள்ளலாம். Family time sacrifice பண்ண வேண்டாம்.'
                    }
                  ].map((item) => (
                    <motion.li
                      key={item.title}
                      className="flex items-start gap-3"
                      variants={itemVariants}
                    >
                      <span className={`${item.color} text-xl mt-1`}>
                        {item.emoji}
                      </span>
                      <div className="flex-1">
                        <span className="font-medium">{item.title}</span>
                        <div className="text-gray-300 mt-1">{item.desc}</div>
                      </div>
                    </motion.li>
                  ))}
                </motion.ul>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mt-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{delay: 0.2, ...sectionFadeIn.transition}}
                >
                  Ungal identity + respect + income-க்கான journey-ஐ start பண்ண இதுவே சரியான வாய்ப்பு. Let's Transform! 🚀
                </motion.p>

                <motion.div
                  className="mt-6 flex flex-wrap gap-4"
                  {...sectionFadeIn}
                  transition={{delay: 0.3, ...sectionFadeIn.transition}}
                >
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-[#fbbf24]" />
                    <span>Women Empowerment Expert</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-[#fbbf24]" />
                    <span>Life Coach Mentor</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-[#fbbf24]" />
                    <span>Tamil Life Coach AI</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <motion.section
          className="testimonials-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎬 Women Life Coach Success Stories - Video Testimonials
              </motion.h2>

              <motion.p
                className="testimonials-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}
              >
                Real results from women who built flexible life coaching careers
              </motion.p>

              <motion.div
                className="video-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}
              >
                {testimonialVideos.map((video) => (
                  <motion.div
                    key={video.id}
                    className="video-card"
                    onClick={() => {
                      setSelectedVideo(video);
                      if (typeof fbq !== 'undefined') {
                        fbq('track', 'ViewContent', {
                          content_name: video.title,
                          content_type: 'video'
                        });
                      }
                    }}
                    variants={itemVariants}
                    whileHover={{scale: 1.02, y: -8}}
                    whileTap={{scale: 0.98}}
                    tabIndex={0}
                    role="button"
                    aria-label={`Play testimonial video from ${video.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedVideo(video);
                      }
                    }}
                  >
                    <div className="video-thumbnail">
                      <img
                        src={video.thumbnail}
                        alt={`${video.title} thumbnail`}
                        loading="lazy"
                        width="300"
                        height="200"
                      />
                      <div className="play-overlay">
                        <FiPlay className="play-icon" />
                      </div>
                    </div>
                    <div className="video-info">
                      <h4>{video.title}</h4>
                      <p>{video.name}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Client Feedback Section - WOMEN FOCUSED */}
        <motion.section
          className="client-feedback-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                What Other Women Life Coaches Say
              </motion.h2>

              <motion.p
                className="feedback-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}
              >
                Join hundreds of women building flexible life coaching careers
              </motion.p>

              <motion.div
                className="feedback-filter"
                {...sectionFadeIn}
                transition={{delay: 0.2, ...sectionFadeIn.transition}}
              >
                <FiFilter className="filter-icon" />
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="role-filter-select"
                  aria-label="Filter by coaching type"
                >
                  {uniqueRoles.map((role) => (
                    <option key={role} value={role}>
                      {role === 'all' ? 'All Life Coach Types' : role}
                    </option>
                  ))}
                </select>
              </motion.div>

              <motion.div
                className="feedback-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}
              >
                {filteredFeedbacks.map((feedback) => (
                  <motion.div
                    key={feedback.id}
                    className="feedback-card"
                    variants={itemVariants}
                    whileHover={{scale: 1.02, y: -8}}
                    whileTap={{scale: 0.98}}
                    tabIndex={0}
                    role="article"
                    aria-labelledby={`feedback-name-${feedback.id}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFeedbackExpanded(feedback.id);
                      }
                    }}
                  >
                    <div className="feedback-image-container">
                      {feedback.image ? (
                        <img
                          src={feedback.image}
                          alt={`${feedback.name} - ${feedback.role}`}
                          loading="lazy"
                          width="300"
                          height="200"
                          className="feedback-rect-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="avatar-fallback-rect">
                        {getInitials(feedback.name)}
                      </div>
                    </div>

                    <div className="feedback-info">
                      <h3 id={`feedback-name-${feedback.id}`}>
                        {feedback.name}
                      </h3>
                      <p className="feedback-role">{feedback.role}</p>
                    </div>

                    <div className="feedback-quote">
                      <p>
                        {expandedFeedback[feedback.id] ||
                        feedback.quote.length <= 140
                          ? feedback.quote
                          : `${feedback.quote.slice(0, 140)}...`}
                      </p>
                      {feedback.quote.length > 140 && (
                        <button
                          className="read-more-btn"
                          onClick={() => toggleFeedbackExpanded(feedback.id)}
                          aria-expanded={!!expandedFeedback[feedback.id]}
                          aria-controls={`feedback-quote-${feedback.id}`}
                        >
                          {expandedFeedback[feedback.id]
                            ? 'Read less'
                            : 'Read more'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Bonus Section - WOMEN LIFE COACH FOCUSED */}
        <motion.section
          className="bonus-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎁 Exclusive Bonus Vault for Women Life Coaches (Worth ₹35,000)
              </motion.h2>

              <motion.div
                className="bonus-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}
              >
                {[
                  {
                    img: 'https://storage.files-vault.com/uploads/1760966854-BXbDBFXvqB.png',
                    emoji: '🤖',
                    title: 'AI Tools for Life Coaches',
                    desc: 'Top 50+ AI tools to automate client management, session scheduling, content creation, and lead generation for women coaches (Worth ₹10,000)'
                  },
                  {
                    img: 'https://storage.files-vault.com/uploads/1760966871-XKw3XTHpgg.png',
                    emoji: '📊',
                    title: 'Life Coaching Templates',
                    desc: 'Ready-to-use templates for coaching frameworks, client worksheets, session planners, and marketing materials (Worth ₹12,000)'
                  },
                  {
                    img: 'https://storage.files-vault.com/uploads/1760976790-fakmsXQpOV.png',
                    emoji: '💬',
                    title: 'Private Women Coaches Community',
                    desc: 'Lifetime access to exclusive Women Life Coaches support group with Tamil AI guidance (Worth ₹8,000)'
                  },
                  {
                    img: 'https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/6974640bc1fa0c55da395de0.png',
                    emoji: '🗺️',
                    title: 'Free Life Coach Roadmap',
                    desc: 'Personalized step-by-step PDF guide to launch your flexible life coaching practice as a woman (Worth ₹5,000)'
                  }
                ].map((bonus) => (
                  <motion.div
                    key={bonus.title}
                    className="bonus-item"
                    variants={itemVariants}
                    whileHover={{y: -8, scale: 1.02}}
                  >
                    <img
                      src={bonus.img}
                      alt={bonus.title}
                      className="bonus-image"
                      width="120"
                      height="160"
                      loading="lazy"
                    />
                    <h3>
                      {bonus.emoji} {bonus.title}
                    </h3>
                    <p>{bonus.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          className="faq-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                Frequently Asked Questions
              </motion.h2>

              <motion.p
                className="faq-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}
              >
                Got questions? We've got answers. Find everything you need to know about our 3-hour workshop.
              </motion.p>

              <motion.div
                className="faq-search"
                {...sectionFadeIn}
                transition={{delay: 0.2, ...sectionFadeIn.transition}}
              >
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={faqSearch}
                  onChange={(e) => handleFaqSearch(e.target.value)}
                  className="search-input"
                  aria-label="Search frequently asked questions"
                />
              </motion.div>

              <div className="faq-accordion">
                {filteredFaqs.map((faq) => (
                  <motion.div key={faq.id} className="faq-item" layout>
                    <button
                      className="faq-question"
                      onClick={() => toggleFaq(faq.id)}
                      aria-expanded={expandedFaq === faq.id}
                      aria-controls={`faq-answer-${faq.id}`}
                      id={`faq-question-${faq.id}`}
                    >
                      <span>{faq.question}</span>
                      <FiChevronDown
                        className={`chevron-icon ${
                          expandedFaq === faq.id ? 'expanded' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {expandedFaq === faq.id && (
                        <motion.div
                          id={`faq-answer-${faq.id}`}
                          className="faq-answer"
                          role="region"
                          aria-labelledby={`faq-question-${faq.id}`}
                          initial={{height: 0, opacity: 0}}
                          animate={{height: 'auto', opacity: 1}}
                          exit={{height: 0, opacity: 0}}
                          transition={{duration: 0.3, ease: 'easeInOut'}}
                        >
                          <div
                            className="faq-answer-content"
                            dangerouslySetInnerHTML={{__html: faq.answer}}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <motion.div
                  className="faq-no-results"
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  transition={{duration: 0.3}}
                >
                  <p>
                    No questions found matching your search. Try different keywords.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          className="final-cta"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="cta-content">
              <motion.h2 {...sectionFadeIn}>
                🔥 Limited Time Offer - Only 50 Seats for Women!
              </motion.h2>

              <motion.p
                className="cta-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}
              >
                3-Hour Workshop Value: ₹4,997 | Today: **100% FREE for Women!**
              </motion.p>

              <WorkshopTimer className="large" />

              <motion.button
                className="cta-primary large"
                onClick={() => {
                  handleManualModalOpen();
                  if (typeof fbq !== 'undefined') {
                    fbq('track', 'InitiateCheckout', {
                      content_name: 'Women Life Coach Workshop - Final CTA',
                      content_category: 'Workshop',
                      value: 0,
                      currency: 'INR'
                    });
                  }
                }}
                {...sectionFadeIn}
                transition={{delay: 0.4, ...sectionFadeIn.transition}}
                whileHover={{scale: 1.05, y: -4}}
                whileTap={{scale: 0.95}}
              >
                Yes! I Want My Identity + Respect Now 🚀
              </motion.button>

              <motion.div
                className="guarantee-text"
                {...sectionFadeIn}
                transition={{delay: 0.5, ...sectionFadeIn.transition}}
              >
                <FiCheck className="check-icon" />
                <span>
                  100% FREE - No Hidden Charges - Flexible & Family-Friendly
                </span>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Registration Modal */}
        <AnimatePresence>
          {isFormModalOpen && (
            <motion.div
              className="modal-overlay"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsFormModalOpen(false);
              }}
            >
              <motion.div
                className="modal-content"
                id="registration-modal"
                initial={{opacity: 0, scale: 0.8, y: 20}}
                animate={{opacity: 1, scale: 1, y: 0}}
                exit={{opacity: 0, scale: 0.8, y: 20}}
                transition={{type: 'spring', stiffness: 300, damping: 25}}
                ref={modalRef}
              >
                <div className="modal-header">
                  <h3>
                    🚀 Register for the FREE 3-Hour Life Coach Workshop for Women!
                  </h3>
                  <button
                    className="close-button"
                    onClick={() => setIsFormModalOpen(false)}
                    aria-label="Close registration form"
                  >
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FiUser className="input-icon" />
                      Full Name *
                    </label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className={formErrors.name ? 'error' : ''}
                      aria-describedby={
                        formErrors.name ? 'name-error' : undefined
                      }
                    />
                    {formErrors.name && (
                      <span id="name-error" className="error-message" role="alert">
                        {formErrors.name}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FiMail className="input-icon" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange('email', e.target.value)
                      }
                      placeholder="Enter your email address"
                      className={formErrors.email ? 'error' : ''}
                      aria-describedby={
                        formErrors.email ? 'email-error' : undefined
                      }
                    />
                    {formErrors.email && (
                      <span
                        id="email-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <FiPhone className="input-icon" />
                      Phone Number *
                    </label>
                    <div className="phone-input-group">
                      <SearchableCountryCodeSelect
                        options={countryCodes}
                        value={formData.countryCode}
                        onChange={(code) =>
                          handleInputChange('countryCode', code)
                        }
                      />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange(
                            'phone',
                            e.target.value.replace(/\D/g, '')
                          )
                        }
                        placeholder="Enter your phone number"
                        className={formErrors.phone ? 'error' : ''}
                        aria-describedby={
                          formErrors.phone ? 'phone-error' : undefined
                        }
                        maxLength="15"
                      />
                    </div>
                    {formErrors.phone && (
                      <span
                        id="phone-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="profession">
                      <FiBriefcase className="input-icon" />
                      Current Situation *
                    </label>
                    <select
                      id="profession"
                      name="profession"
                      value={formData.profession}
                      onChange={(e) =>
                        handleInputChange('profession', e.target.value)
                      }
                      className={formErrors.profession ? 'error' : ''}
                      aria-describedby={
                        formErrors.profession ? 'profession-error' : undefined
                      }
                    >
                      <option value="">Select your current situation</option>
                      <option value="Housewife">Housewife</option>
                      <option value="Working Woman">Working Woman</option>
                      <option value="Part-Time Worker">Part-Time Worker</option>
                      <option value="Stay-at-home Mother">
                        Stay-at-home Mother
                      </option>
                      <option value="Career Break">Career Break</option>
                      <option value="Aspiring Entrepreneur">
                        Aspiring Entrepreneur
                      </option>
                      <option value="Student">Student</option>
                      <option value="Other">Other</option>
                    </select>
                    {formErrors.profession && (
                      <span
                        id="profession-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.profession}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) =>
                          handleInputChange('consent', e.target.checked)
                        }
                        className={formErrors.consent ? 'error' : ''}
                        aria-describedby={
                          formErrors.consent ? 'consent-error' : undefined
                        }
                      />
                      <span className="checkmark"></span>I agree to receive
                      updates about the program and understand the terms &
                      conditions *
                    </label>
                    {formErrors.consent && (
                      <span
                        id="consent-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.consent}
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'Processing...'
                      : 'Register Now - 100% FREE 🚀'}
                  </button>

                  <p className="form-note">
                    After registration, you'll receive workshop details via email
                    and WhatsApp.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Modal */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              className="video-modal-overlay"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedVideo(null);
              }}
            >
              <motion.div
                className="video-modal-content"
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                exit={{opacity: 0, scale: 0.8}}
                transition={{type: 'spring', stiffness: 300, damping: 25}}
              >
                <div className="video-modal-header">
                  <h3>{selectedVideo.title}</h3>
                  <button
                    className="close-button"
                    onClick={() => setSelectedVideo(null)}
                    aria-label="Close video"
                  >
                    <FiX />
                  </button>
                </div>
                <div className="video-embed-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.embedId}?autoplay=1&rel=0`}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                <div className="video-fallback">
                  <p>
                    Can't view the video?{' '}
                    <a
                      href={selectedVideo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open on YouTube
                    </a>
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sticky Footer */}
        <motion.div
          className="sticky-footer"
          initial={{y: '100%'}}
          animate={{y: '0%'}}
          transition={{duration: 0.7, ease: 'easeInOut', delay: 1.5}}
        >
          <motion.div
            className="footer-info-box"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.5, delay: 2.2}}
          >
            <div className="date">Next Workshop:</div>
            <div className="time">{workshopDetails.formattedDate}</div>
          </motion.div>

          <motion.button
            className="footer-cta-button"
            onClick={() => {
              handleManualModalOpen();
              if (typeof fbq !== 'undefined') {
                fbq('track', 'InitiateCheckout', {
                  content_name:
                    'Women Life Coach Workshop - Sticky Footer CTA',
                  content_category: 'Workshop',
                  value: 0,
                  currency: 'INR'
                });
              }
            }}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            animate={{
              scale: [1, 1.03, 1],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2.5
              }
            }}
          >
            Register FREE Now 🚀
          </motion.button>
        </motion.div>

        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <p>
                &copy; 2026 10xAI Women Life Coach Empowerment. All rights
                reserved.
              </p>
              <p
                style={{
                  fontSize: '0.8rem',
                  opacity: '0.7',
                  marginTop: '1rem'
                }}
              >
                This site is not part of the Facebook website or Facebook Inc.
                Additionally, this site is NOT endorsed by Facebook. FACEBOOK is a
                trademark of FACEBOOK, Inc.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default App;